import { type PropsWithChildren, memo, useMemo } from "react";

import {
  type WithBreakpoints,
  getSizeWithBreakpoints,
} from "@react/utilities/responsive";
import { type TTailwindString, tw } from "classnames";

import { HeadingView } from "./Heading.view";

type HeadingLevel =
  | "display"
  | "h1"
  | "h2"
  | "h3"
  | "h4"
  | "h5"
  | "h6"
  | "span";
export type HeadingSize = "display" | "xxl" | "xl" | "lg" | "md" | "sm" | "xs";

type Props = {
  as: HeadingLevel;
  className?: TTailwindString;
  id?: string;
  size?: HeadingSize | WithBreakpoints<HeadingSize>;
};

const defaultSizes = {
  display: "display",
  h1: "xxl",
  h2: "xl",
  h3: "lg",
  h4: "md",
  h5: "sm",
  h6: "xs",
  span: "xs",
} as const;

const styles = {
  sm: {
    display: tw("text-8xl", "font-light", "leading-tight"),
    xxl: tw("text-7xl", "font-light", "leading-tight"),
    xl: tw("text-5xl", "font-light", "leading-tight"),
    lg: tw("text-4xl", "font-light", "leading-tight"),
    md: tw("text-2xl", "font-normal", "leading-normal"),
    sm: tw("text-xl", "font-normal", "leading-relaxed"),
    xs: tw("text-base", "font-normal", "leading-relaxed"),
  },
  md: {
    display: tw("md:text-10xl", "md:font-light", "md:leading-tight"),
    xxl: tw("md:text-9xl", "md:font-light", "md:leading-tight"),
    xl: tw("md:text-8xl", "md:font-light", "md:leading-tight"),
    lg: tw("md:text-6xl", "md:font-light", "md:leading-tight"),
    md: tw("md:text-4xl", "md:font-normal", "md:leading-normal"),
    sm: tw("md:text-2xl", "md:font-normal", "md:leading-relaxed"),
    xs: tw("md:text-base", "md:font-normal", "md:leading-relaxed"),
  },
  mg: {
    display: tw("mg:text-10xl", "mg:font-light", "mg:leading-tight"),
    xxl: tw("mg:text-9xl", "mg:font-light", "mg:leading-tight"),
    xl: tw("mg:text-8xl", "mg:font-light", "mg:leading-tight"),
    lg: tw("mg:text-6xl", "mg:font-light", "mg:leading-tight"),
    md: tw("mg:text-4xl", "mg:font-normal", "mg:leading-normal"),
    sm: tw("mg:text-2xl", "mg:font-normal", "mg:leading-relaxed"),
    xs: tw("mg:text-base", "mg:font-normal", "mg:leading-relaxed"),
  },
  lg: {
    display: tw("lg:text-10xl", "lg:font-light", "lg:leading-tight"),
    xxl: tw("lg:text-9xl", "lg:font-light", "lg:leading-tight"),
    xl: tw("lg:text-8xl", "lg:font-light", "lg:leading-tight"),
    lg: tw("lg:text-6xl", "lg:font-light", "lg:leading-tight"),
    md: tw("lg:text-4xl", "lg:font-normal", "lg:leading-normal"),
    sm: tw("lg:text-2xl", "lg:font-normal", "lg:leading-relaxed"),
    xs: tw("lg:text-base", "lg:font-normal", "lg:leading-relaxed"),
  },
  xl: {
    display: tw("xl:text-10xl", "xl:font-light", "xl:leading-tight"),
    xxl: tw("xl:text-9xl", "xl:font-light", "xl:leading-tight"),
    xl: tw("xl:text-8xl", "xl:font-light", "xl:leading-tight"),
    lg: tw("xl:text-6xl", "xl:font-light", "xl:leading-tight"),
    md: tw("xl:text-4xl", "xl:font-normal", "xl:leading-normal"),
    sm: tw("xl:text-2xl", "xl:font-normal", "xl:leading-relaxed"),
    xs: tw("xl:text-base", "xl:font-normal", "xl:leading-relaxed"),
  },
};

const HeadingBase = ({
  as,
  children,
  className,
  id,
  size: originalSize,
}: PropsWithChildren<Props>) => {
  const resolvedAs = as === "display" ? "h1" : as;

  const size = useMemo(() => {
    const resolvedSize =
      typeof originalSize === "string"
        ? { sm: originalSize, md: originalSize }
        : { sm: defaultSizes[as], md: defaultSizes[as], ...originalSize };

    return getSizeWithBreakpoints(resolvedSize);
  }, [as, originalSize]);

  const headingClass = useMemo(
    () =>
      tw(
        "font-chelt",
        "antialiased",
        "m-0",
        "p-0",
        {
          [styles.sm.display]: size.sm === "display",
          [styles.sm.xxl]: size.sm === "xxl",
          [styles.sm.xl]: size.sm === "xl",
          [styles.sm.lg]: size.sm === "lg",
          [styles.sm.md]: size.sm === "md",
          [styles.sm.sm]: size.sm === "sm",
          [styles.sm.xs]: size.sm === "xs",
        },
        {
          [styles.md.display]: size.md === "display",
          [styles.md.xxl]: size.md === "xxl",
          [styles.md.xl]: size.md === "xl",
          [styles.md.lg]: size.md === "lg",
          [styles.md.md]: size.md === "md",
          [styles.md.sm]: size.md === "sm",
          [styles.md.xs]: size.md === "xs",
        },
        {
          [styles.mg.display]: size.mg === "display",
          [styles.mg.xxl]: size.mg === "xxl",
          [styles.mg.xl]: size.mg === "xl",
          [styles.mg.lg]: size.mg === "lg",
          [styles.mg.md]: size.mg === "md",
          [styles.mg.sm]: size.mg === "sm",
          [styles.mg.xs]: size.mg === "xs",
        },
        {
          [styles.lg.display]: size.lg === "display",
          [styles.lg.xxl]: size.lg === "xxl",
          [styles.lg.xl]: size.lg === "xl",
          [styles.lg.lg]: size.lg === "lg",
          [styles.lg.md]: size.lg === "md",
          [styles.lg.sm]: size.lg === "sm",
          [styles.lg.xs]: size.lg === "xs",
        },
        {
          [styles.xl.display]: size.xl === "display",
          [styles.xl.xxl]: size.xl === "xxl",
          [styles.xl.xl]: size.xl === "xl",
          [styles.xl.lg]: size.xl === "lg",
          [styles.xl.md]: size.xl === "md",
          [styles.xl.sm]: size.xl === "sm",
          [styles.xl.xs]: size.xl === "xs",
        },
        className ? tw(className) : undefined
      ),
    [className, size.sm, size.md, size.mg, size.lg, size.xl]
  );

  return (
    <HeadingView as={resolvedAs} className={headingClass} id={id}>
      {children}
    </HeadingView>
  );
};

export const Heading = memo(HeadingBase);
